import type { ITreeNodeItem } from "./types";

export const getInitialExpandedIds = (
  selectedId: ITreeNodeItem["id"],
  nodes: ITreeNodeItem[],
): ITreeNodeItem["id"][] =>
  nodes
    .map((node) => {
      if (node.id === selectedId) {
        return [node.id];
      }

      if (node.children) {
        const path = getInitialExpandedIds(selectedId, node.children);
        return path.length > 0 ? [node.id, ...path] : [];
      }

      return [];
    })
    .flat();

export const getAllExpandableIds = (
  nodes: ITreeNodeItem[],
): ITreeNodeItem["id"][] => {
  const ids: ITreeNodeItem["id"][] = [];

  const traverse = (items: ITreeNodeItem[]) => {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        ids.push(item.id);
        traverse(item.children);
      }
    }
  };

  traverse(nodes);
  return ids;
};

/**
 * Recursively collects all IDs from tree nodes and their descendants.
 * Unlike getAllExpandableIds, this includes ALL nodes (not just those with children).
 */
export const getAllDescendantIds = (
  nodes: ITreeNodeItem[],
): Set<ITreeNodeItem["id"]> => {
  const ids = new Set<ITreeNodeItem["id"]>();

  const traverse = (items: ITreeNodeItem[]) => {
    for (const item of items) {
      ids.add(item.id);
      if (item.children && item.children.length > 0) {
        traverse(item.children);
      }
    }
  };

  traverse(nodes);
  return ids;
};

/**
 * The roving-tabindex default target: the selected item if it's actually
 * part of this tree's own data, otherwise the first top-level item. A
 * `selectedId` that belongs to a *different* tree instance (e.g. the
 * navbar's Trash tree is handed the app's globally-selected collection id,
 * which is never one of Trash's own items) must not be used as-is — it
 * would never match any item this tree renders, leaving nothing tabbable
 * at all.
 */
export const getDefaultTabbableId = (
  data: ITreeNodeItem[],
  selectedId: ITreeNodeItem["id"] | undefined,
): ITreeNodeItem["id"] | undefined => {
  if (selectedId != null && getAllDescendantIds(data).has(selectedId)) {
    return selectedId;
  }
  return data[0]?.id;
};

const TREE_ITEM_SELECTOR = '[role="menuitem"], [role="treeitem"]';
const TREE_ROOT_SELECTOR = '[role="tree"], [role="menu"]';

/**
 * Moves focus (and, via the item's own onFocus, the roving tabindex target)
 * to the previous/next visible tree item, relative to `currentElement` (any
 * element inside the current item). Tree items render as a flat,
 * nested-by-indentation DOM list, so DOM order already matches visual order
 * for whatever is currently expanded.
 *
 * Every TreeNode implementation puts `role="treeitem"`/`"menuitem"` and the
 * roving `tabIndex` (0 for the current item, -1 for the rest) on the same
 * element, so that element is always a valid `.focus()` target regardless
 * of which of the two values its tabIndex currently holds.
 */
export function focusAdjacentTreeItem(
  currentElement: HTMLElement,
  direction: 1 | -1,
) {
  const currentItem = currentElement.closest<HTMLElement>(TREE_ITEM_SELECTOR);
  const treeRoot = currentElement.closest<HTMLElement>(TREE_ROOT_SELECTOR);
  if (!currentItem || !treeRoot) {
    return;
  }

  const items = Array.from(
    treeRoot.querySelectorAll<HTMLElement>(TREE_ITEM_SELECTOR),
  );
  const nextItem = items[items.indexOf(currentItem) + direction];
  nextItem?.focus();
}
